/**
 * `AppError` is the single error type the data layer hands to the UI.
 *
 * Every client — mock, HTTP, future WebSocket — maps its failures into one
 * of the subclasses here. UI code should pattern-match on `kind`, never on
 * a message string.
 */
export type AppErrorKind =
  | 'validation'
  | 'auth'
  | 'forbidden'
  | 'not-found'
  | 'conflict'
  | 'rate-limited'
  | 'network'
  | 'timeout'
  | 'server'
  | 'unknown';

export interface AppErrorDetails {
  /** Machine-readable identifier if the server provides one. */
  code?: string;
  /** Field-level validation failures keyed by dotted path. */
  fields?: Record<string, string[]>;
  /** Arbitrary safe metadata. Must not contain secrets or PII. */
  meta?: Record<string, unknown>;
  /** Original cause — never shown to users, only surfaced in logs. */
  cause?: unknown;
}

export class AppError extends Error {
  readonly kind: AppErrorKind;
  readonly status?: number;
  readonly details: AppErrorDetails;

  constructor(
    kind: AppErrorKind,
    message: string,
    options?: { status?: number; details?: AppErrorDetails },
  ) {
    super(message);
    this.name = 'AppError';
    this.kind = kind;
    this.status = options?.status;
    this.details = options?.details ?? {};
    if (options?.details?.cause !== undefined) {
      (this as { cause?: unknown }).cause = options.details.cause;
    }
  }
}

export class ValidationError extends AppError {
  constructor(message: string, details?: AppErrorDetails) {
    super('validation', message, { status: 400, details });
    this.name = 'ValidationError';
  }
}

export class AuthError extends AppError {
  constructor(message = 'Authentication required', details?: AppErrorDetails) {
    super('auth', message, { status: 401, details });
    this.name = 'AuthError';
  }
}

export class ForbiddenError extends AppError {
  constructor(message = 'Forbidden', details?: AppErrorDetails) {
    super('forbidden', message, { status: 403, details });
    this.name = 'ForbiddenError';
  }
}

export class NotFoundError extends AppError {
  constructor(message = 'Not found', details?: AppErrorDetails) {
    super('not-found', message, { status: 404, details });
    this.name = 'NotFoundError';
  }
}

export class ConflictError extends AppError {
  constructor(message = 'Conflict', details?: AppErrorDetails) {
    super('conflict', message, { status: 409, details });
    this.name = 'ConflictError';
  }
}

export class RateLimitedError extends AppError {
  constructor(message = 'Rate limited', details?: AppErrorDetails) {
    super('rate-limited', message, { status: 429, details });
    this.name = 'RateLimitedError';
  }
}

export class NetworkError extends AppError {
  constructor(message = 'Network error', details?: AppErrorDetails) {
    super('network', message, { details });
    this.name = 'NetworkError';
  }
}

export class TimeoutError extends AppError {
  constructor(message = 'Request timed out', details?: AppErrorDetails) {
    super('timeout', message, { details });
    this.name = 'TimeoutError';
  }
}

export class ServerError extends AppError {
  constructor(
    message = 'Server error',
    options?: { status?: number; details?: AppErrorDetails },
  ) {
    super('server', message, { status: options?.status ?? 500, details: options?.details });
    this.name = 'ServerError';
  }
}

export class UnknownError extends AppError {
  constructor(message = 'Unknown error', details?: AppErrorDetails) {
    super('unknown', message, { details });
    this.name = 'UnknownError';
  }
}

/**
 * Wrap any thrown value and surface it as an AppError. The original cause
 * is preserved on `.details.cause` for logging.
 */
export function toAppError(err: unknown): AppError {
  if (err instanceof AppError) return err;
  if (err instanceof Error) {
    return new UnknownError(err.message, { cause: err });
  }
  return new UnknownError('Unknown error', { cause: err });
}

export function isAppError(err: unknown): err is AppError {
  return err instanceof AppError;
}
